/**
 * Metric Event Constants
 *
 * The 27 commercially-important events for Creately AI.
 * See docs/metrics-tracking.md for full documentation.
 *
 * CORE EVENTS (12) - Business Outcomes
 * FEEDER EVENTS (15) - Diagnostics & Predictions
 */

// ==========================================
// CORE EVENTS - ACTIVATION (Business Outcomes)
// ==========================================
export const ACTIVATION = {
  /** User completed signup (non-demo) */
  SIGNUP_COMPLETE: 'activation.signup.complete',
  /** User created first genogram with 3+ shapes */
  FIRST_GENOGRAM: 'activation.first_genogram',
} as const;

// ==========================================
// CORE EVENTS - CONVERSION (Revenue)
// ==========================================
export const CONVERSION = {
  /** User hit export paywall */
  EXPORT_BLOCKED: 'conversion.export.blocked',
  /** User started upgrade flow */
  UPGRADE_STARTED: 'conversion.upgrade.started',
  /** User completed upgrade */
  UPGRADE_COMPLETE: 'conversion.upgrade.complete',
} as const;

// ==========================================
// CORE EVENTS - SESSION (Retention)
// ==========================================
export const SESSION = {
  /** Session started */
  START: 'session.start',
  /** Session summary (aggregated counts) */
  SUMMARY: 'session.summary',
} as const;

// ==========================================
// CORE EVENTS - VALUE DELIVERY
// ==========================================
export const VALUE = {
  /** User completed an export */
  EXPORT_COMPLETED: 'export.completed',
} as const;

// ==========================================
// CORE EVENTS - AI
// ==========================================
export const AI = {
  /** AI generated a draft */
  DRAFT_GENERATED: 'ai.draft.generated',
  /** User accepted AI draft */
  DRAFT_ACCEPTED: 'ai.draft.accepted',
} as const;

// ==========================================
// CORE EVENTS - ERRORS
// ==========================================
export const ERRORS = {
  /** Critical error occurred */
  CRITICAL: 'error.critical',
} as const;

// ==========================================
// FEEDER EVENTS - ACTIVATION FUNNEL
// ==========================================
export const ACTIVATION_FUNNEL = {
  /** Use-case picker shown (impression — funnel denominator) */
  USE_CASE_PICKER_SHOWN: 'onboarding.use_case_picker_shown',
  /** User selected a use case during onboarding */
  USE_CASE_SELECTED: 'onboarding.use_case_selected',
  /** User selected a template */
  TEMPLATE_SELECTED: 'onboarding.template_selected',
  /** User created their first shape */
  FIRST_SHAPE: 'shape.first_created',
  /** User used quick-add for the first time */
  QUICK_ADD_FIRST: 'quick_add.first_used',
} as const;

// ==========================================
// FEEDER EVENTS - CONVERSION FUNNEL
// ==========================================
export const CONVERSION_FUNNEL = {
  /** User opened export modal */
  EXPORT_MODAL_OPENED: 'export.modal_opened',
  /** User selected an export format */
  EXPORT_FORMAT_SELECTED: 'export.format_selected',
  /** User dismissed upgrade modal */
  UPGRADE_DISMISSED: 'upgrade.modal_dismissed',
} as const;

export const COMMERCIAL_ACCESS = {
  /** A safe synthetic product preview was rendered for denied Advanced access. */
  LOCKED_PREVIEW_VIEWED: 'commercial.locked_preview.viewed',
  /** A user followed the contextual plan or Enterprise-sales path. */
  LOCKED_PREVIEW_CTA_CLICKED: 'commercial.locked_preview.cta_clicked',
} as const;

export const REPORT_EXPORT = {
  /** User opened genogram report generation modal */
  MODAL_OPENED: 'export.report_modal_opened',
  /** User confirmed start for a report export job */
  STARTED: 'export.report_started',
} as const;

// ==========================================
// FEEDER EVENTS - RETENTION PREDICTORS
// ==========================================
export const RETENTION = {
  /** User reopened a diagram */
  DIAGRAM_REOPENED: 'diagram.reopened',
  /** User shared a diagram */
  SHARE_COMPLETED: 'share.completed',
} as const;

// ==========================================
// FEEDER EVENTS - AI DIAGNOSTICS
// ==========================================
export const AI_FUNNEL = {
  /** User submitted an AI prompt */
  PROMPT_SUBMITTED: 'ai.prompt_submitted',
  /** AI suggestion was shown */
  SUGGESTION_SHOWN: 'ai.suggestion_shown',
  /** AI encountered an error */
  ERROR: 'ai.error',
} as const;

// ==========================================
// FEEDER EVENTS - VALUE PREDICTORS
// ==========================================
export const VALUE_PREDICTORS = {
  /** User reached 3+ generations in genogram */
  DEPTH_MILESTONE: 'genogram.depth_milestone',
  /** User filled person details */
  DETAILS_FILLED: 'person.details_filled',
  /** Multi-user session started */
  COLLABORATION_STARTED: 'collaboration.started',
} as const;

// ==========================================
// TABLE EVENTS - PHASE 1: Core Table (MVP)
// ==========================================
export const TABLE = {
  /** User created a new table (direct - low volume, high value) */
  CREATED: 'table.created',
  /** User deleted a table */
  DELETED: 'table.deleted',
  /** User created their first table ever (first_time) */
  FIRST_CREATED: 'table.first_created',
} as const;

// ==========================================
// TABLE EVENTS - PHASE 2: Advanced Layout
// ==========================================
export const TABLE_ADVANCED = {
  /** User merged cells (direct - important action) */
  CELLS_MERGED: 'table.cells.merged',
  /** User unmerged cells */
  CELLS_UNMERGED: 'table.cells.unmerged',
  /** User toggled header row/column */
  HEADER_TOGGLED: 'table.header.toggled',
  /** User dropped content into a cell (direct - feature adoption) */
  CONTENT_DROPPED: 'table.content.dropped',
  /** User created table from schema template (direct - template usage) */
  SCHEMA_CREATED: 'table.schema.created',
} as const;

// ==========================================
// TABLE SESSION METRICS (Aggregated in session.summary)
// ==========================================
export const TABLE_SESSION_METRICS = {
  /** Count of rows added during session */
  ROWS_ADDED: 'table_rows_added',
  /** Count of rows removed during session */
  ROWS_REMOVED: 'table_rows_removed',
  /** Count of columns added during session */
  COLS_ADDED: 'table_cols_added',
  /** Count of columns removed during session */
  COLS_REMOVED: 'table_cols_removed',
  /** Count of cell text edits during session */
  CELLS_EDITED: 'table_cells_edited',
  /** Count of row/column resizes during session */
  RESIZES: 'table_resizes',
  /** Count of cell/row/column color changes during session */
  COLORS_APPLIED: 'table_colors_applied',
  /** Count of text rotation changes during session */
  TEXT_ROTATIONS: 'table_text_rotations',
  /** Count of row/column reorders during session */
  REORDERS: 'table_reorders',
  /** Count of multi-cell selections during session */
  MULTI_SELECTIONS: 'table_multi_selections',

} as const;

// ==========================================
// FEEDER EVENTS - MEDICAL FIELDS
// ==========================================
export const MEDICAL = {
  /** User toggled a medical field option (selected/deselected) */
  FIELD_OPTION_TOGGLED: 'medical.field.option_toggled',
  /** User created a custom medical field option */
  FIELD_CUSTOM_OPTION_CREATED: 'medical.field.custom_option_created',
  /** User opened a medical section in property panel */
  SECTION_OPENED: 'medical.section.opened',
} as const;

// ==========================================
// FEEDER EVENTS - GENOGRAM AI (assistant generate flow)
// ==========================================
export const GENOGRAM_AI = {
  /** User toggled "Use Basic Shapes" in Genogram Assistant (generate from text) */
  ASSISTANT_BASIC_SHAPE_TOGGLED: 'genogram.assistant.basic_shape_toggled',
} as const;

export const GENOGRAM_WIZARD = {
  /** Geno wizard dialog became visible */
  VIEWED: 'genogram_wizard_viewed',
  /** User selected a use case option in the geno wizard */
  OPTION_SELECTED: 'genogram_wizard_option_selected',
  /** User continued with the starter template from the geno wizard */
  TEMPLATE_STARTED: 'genogram_wizard_template_started',
  /** App loaded with a geno wizard handoff context */
  APP_LOAD: 'genogram_wizard_app_load',
} as const;

export const HIERARCHY_CHART_WIZARD = {
  /** App loaded with a hierarchy chart wizard handoff context */
  APP_LOAD: 'hierarchy_chart_wiz.app_load',
  /** Hierarchy chart wizard dialog became visible */
  VIEWED: 'hierarchy_chart_wiz.viewed',
  /** User selected a structure option in the hierarchy chart wizard */
  OPTION_SELECTED: 'hierarchy_chart_wiz.option_selected',
  /** User continued to the template picker from the hierarchy chart wizard */
  TEMPLATE_STARTED: 'hierarchy_chart_wiz.template_started',
} as const;

// ==========================================
// ORGCHART SESSION FLAGS
// Fire once per session. After the user does it once we know they
// know the feature — no need to log again in the same session.
// ==========================================
export const ORGCHART = {
  /** User completed a CSV import into an org chart */
  CSV_IMPORTED: 'orgchart.csv_imported',
  /** User applied a view preset (color-by, heatmap, etc.) */
  VIEW_PRESET_APPLIED: 'orgchart.view_preset_applied',
  /** User applied a what-if scenario */
  SCENARIO_APPLIED: 'orgchart.scenario_applied',
  /** User had 5+ turns with the AI assistant in a single session */
  AI_CHAT_DEPTH: 'orgchart.ai_chat_depth',
} as const;

// ==========================================
// NETWORK / SYSTEMS MAPPING EVENTS
// ==========================================
export const NETWORK = {
  /** Brand-new Systems Mapping diagram was created (fires once per diagram lifetime) */
  DIAGRAM_CREATED: 'network.diagram.created',
  /** User switched the active view profile (Topology / Security / Cost) */
  PROFILE_SWITCHED: 'network.profile.switched',
  /** User drilled through from a node to its linked bundle view */
  DRILL_THROUGH_USED: 'network.drill_through.used',
  /** User clicked a bundle navigator tree node to open a linked view */
  BUNDLE_NAVIGATED: 'network.bundle.navigated',
  /** Cloud inventory import completed (csv / terraform / cfn / aws-config / aws-scan) */
  IMPORT_COMPLETED: 'network.import.completed',
  /** Cloud inventory import failed parse / preview */
  IMPORT_FAILED: 'network.import.failed',
  /** Agent tool was invoked against the network diagram */
  AI_TOOL_INVOKED: 'network.ai.tool_invoked',
  /** Scenario was created (source: manual | iac-diff) */
  SCENARIO_CREATED: 'network.scenario.created',
  /** Scenario was activated as the current projection */
  SCENARIO_ACTIVATED: 'network.scenario.activated',
  /** Scenario was discarded */
  SCENARIO_DISCARDED: 'network.scenario.discarded',
  /** Scenario delta was applied to the live diagram */
  SCENARIO_APPLIED: 'network.scenario.applied',
  /** Scenario apply failed — fires alongside the thrown error */
  SCENARIO_APPLY_FAILED: 'network.scenario.apply_failed',

  /** User opened the "Fork as design diagram…" modal (Design Loop DL-1). */
  DESIGN_FORK_OPENED: 'network.design.fork_opened',
  /** Fork-as-design completed; payload: { childFolderId, designDiagramId, durationMs }. */
  DESIGN_FORK_COMPLETED: 'network.design.fork_completed',
  /** Fork-as-design failed; payload: { parentFolderId, error }. */
  DESIGN_FORK_FAILED: 'network.design.fork_failed',
  /** DesignChangesBanner rendered with the live diff summary (debounced). */
  DESIGN_DIFF_VIEWED: 'network.design.diff_viewed',

  /** Design-mode `create_resource` agent tool invoked successfully. Payload: { resourceType, surface: 'chat'|'chip' }. */
  DESIGN_CREATE_RESOURCE: 'network.design.create_resource',
  /** Design-mode `connect_resources` agent tool invoked successfully. Payload: { kind, fromType, toType }. */
  DESIGN_CONNECT_RESOURCES: 'network.design.connect_resources',
  /** Design-mode `remove_resource` agent tool invoked successfully. Payload: { resourceType }. */
  DESIGN_REMOVE_RESOURCE: 'network.design.remove_resource',
  /** Design-mode `update_resource_field` agent tool invoked successfully. Payload: { field }. */
  DESIGN_UPDATE_FIELD: 'network.design.update_field',
  /** `validate_diagram` run completed. Payload: { score, errorCount, warningCount }. */
  DESIGN_VALIDATE_RUN: 'network.design.validate_run',
  /** `clone_subtree` ran successfully. Payload: { sourceType, count, clonedEdges }. */
  DESIGN_CLONE_SUBTREE: 'network.design.clone_subtree',
  /** `apply_pattern` ran successfully (post-confirm). Payload: { patternId, resourceCount }. */
  DESIGN_APPLY_PATTERN: 'network.design.apply_pattern',
  /** `compose_service` ran successfully (post-confirm). Payload: { expandedResourceCount }. */
  DESIGN_COMPOSE_SERVICE: 'network.design.compose_service',
  /** `audit_proposed` run completed. Payload: { presetCount, totalFindings }. */
  DESIGN_AUDIT_PROPOSED: 'network.design.audit_proposed',
} as const;

// ==========================================
// DATA BINDING EVENTS
// ==========================================
export const DATA_BINDING = {
  /** Repository/data source was connected to a diagram */
  SOURCE_CONNECTED: 'data_binding.source.connected',
  /** Commit operation started */
  COMMIT_TRIGGERED: 'data_binding.commit.triggered',
  /** Commit operation completed successfully */
  COMMIT_COMPLETED: 'data_binding.commit.completed',
  /** Commit operation failed */
  COMMIT_FAILED: 'data_binding.commit.failed',
  /** Pull operation started */
  PULL_TRIGGERED: 'data_binding.pull.triggered',
  /** Pull operation completed successfully */
  PULL_COMPLETED: 'data_binding.pull.completed',
  /** Pull operation failed */
  PULL_FAILED: 'data_binding.pull.failed',
  /** Discard operation started */
  DISCARD_TRIGGERED: 'data_binding.discard.triggered',
  /** Discard operation completed successfully */
  DISCARD_COMPLETED: 'data_binding.discard.completed',
  /** Discard operation failed */
  DISCARD_FAILED: 'data_binding.discard.failed',
  /** Repository/data source was deleted/unbound from a diagram */
  SOURCE_DELETED: 'data_binding.source.deleted',
  /** Repository/data source delete/unbind failed */
  SOURCE_DELETE_FAILED: 'data_binding.source.delete_failed',
} as const;

// ==========================================
// PROCESS MAPPING EVENTS
// ==========================================
export const PROCESS = {
  /** Brand-new process / flowchart diagram was created (fires once per diagram lifetime) — `metric.activation.processFlowCreated` */
  DIAGRAM_CREATED: 'process.diagram.created',
  /** User returned to a process diagram in a later session — `metric.process.returnUsage` */
  RETURN_USAGE: 'process.return_usage',
  /** User inserted a step via the inline insert-step affordance — `metric.process.insertStepUsed` */
  STEP_INSERTED: 'process.step.inserted',
  /** Auto-layout was applied to a process flow — `metric.process.autoLayoutApplied` */
  AUTO_LAYOUT_APPLIED: 'process.auto_layout.applied',
  /** A BPMN-tagged diagram was created — `metric.process.bpmnDiagramsCreated` */
  BPMN_DIAGRAM_CREATED: 'process.bpmn.diagram_created',
  /** BPMN XML export completed — `metric.process.bpmnExportCount` */
  BPMN_EXPORTED: 'process.bpmn.exported',
  /** BPMN XML import completed — `metric.process.bpmnImportCount` */
  BPMN_IMPORTED: 'process.bpmn.imported',
  /** A subprocess shape was linked to a child diagram — `metric.process.subProcessLinked` */
  SUB_PROCESS_LINKED: 'process.sub_process.linked',
  /** User drilled down into a linked subprocess — `metric.process.drillDownUsed` */
  DRILL_DOWN_USED: 'process.drill_down.used',
  /** AI generated a process flow (or appended to one) — `metric.process.aiGenerationCount` */
  AI_GENERATED: 'process.ai.generated',
  /** Process analysis was run on a flow — `metric.process.analysisRun` */
  ANALYSIS_RUN: 'process.analysis.run',
  /** User applied an action surfaced by a detected pattern — `metric.process.patternActionsApplied` */
  PATTERN_ACTION_APPLIED: 'process.pattern.action_applied',
  /** A field-, statement-, item-, or relationship-level repository proposal was created. */
  GRANULAR_SUGGESTION_CREATED: 'process.repository.granular_suggestion.created',
  /** A reviewer accepted, rejected, or revised a granular repository proposal. */
  GRANULAR_SUGGESTION_REVIEWED: 'process.repository.granular_suggestion.reviewed',
  /** Review was blocked because the exact target changed after proposal creation. */
  GRANULAR_SUGGESTION_STALE: 'process.repository.granular_suggestion.stale',
} as const;

// ==========================================
// PROCESS-DOC JOB LIFECYCLE EVENTS (Import Review)
// ==========================================
// Per `cap.processDoc.*` capabilities. Surface lives at /imports/
// (separate Vite entry). Free-rider Tracker wire-ups in Phase 1.
export const PROCESS_DOC = {
  /** Job row created via POST /api/v1/process-doc-jobs */
  JOB_CREATED: 'process_doc.job.created',
  /** Job dispatched to the queue runner. */
  JOB_DISPATCHED: 'process_doc.job.dispatched',
  /** Job cancelled by the reviewer. */
  JOB_CANCELLED: 'process_doc.job.cancelled',
  /** Job moved to terminal `published` state. */
  JOB_PUBLISHED: 'process_doc.job.published',
  /** Job's `apply` step materialised the folder + records. */
  JOB_APPLIED: 'process_doc.job.applied',
  /** Corpus plan approved → enrichment begins. */
  PLAN_APPROVED: 'process_doc.plan.approved',
  /** Plan revised with reviewer overrides → planning re-runs. */
  PLAN_REVISED: 'process_doc.plan.revised',
  /** Per-item review decision recorded (accept / revise / reject). */
  REVIEW_DECISION_RECORDED: 'process_doc.review_decision.recorded',
  /** Enrichment phase started for a planned job. */
  ENRICHMENT_STARTED: 'process_doc.enrichment.started',
  /** Enrichment skipped (user / budget / no GRC folder). */
  ENRICHMENT_SKIPPED: 'process_doc.enrichment.skipped',
  /** Enrichment retried for a single canonical process. */
  ENRICHMENT_RETRIED: 'process_doc.enrichment.retried',
  /** User browsed artifact outputs in the Outputs tab. */
  ARTIFACTS_BROWSED: 'process_doc.artifacts.browsed',
} as const;

// ==========================================
// PROCESS EXTRACTION EVENTS (PDF → graph pipeline)
// ==========================================
export const PROCESS_EXTRACTION = {
  /** Extraction job finished end-to-end — `metric.process.extraction.jobsCompleted` */
  JOB_COMPLETED: 'process.extraction.job_completed',
  /** Audit trail tab opened for a job — `metric.process.extraction.auditTrailViewed` */
  AUDIT_TRAIL_VIEWED: 'process.extraction.audit_trail.viewed',
  /** Reviewer resolved a finding flagged by the critic loop — `metric.process.extraction.findingsResolvedByUser` */
  FINDING_RESOLVED: 'process.extraction.finding.resolved',
  /** Job rejected for being image-only PDF — `metric.process.extraction.imagePdfRejectionRate` */
  IMAGE_PDF_REJECTED: 'process.extraction.image_pdf.rejected',
  /** Source-fidelity report opened in the source review drawer. */
  SOURCE_FIDELITY_VIEWED: 'process.extraction.source_fidelity.viewed',
  /** A completed import surfaced as draft/untrusted in the folder review queue. */
  SOURCE_FIDELITY_ISSUE_SURFACED: 'process.extraction.source_fidelity.issue_surfaced',
} as const;

// ==========================================
// COMPASS EVENTS (review-state cockpit, audit, federation)
// ==========================================
export const COMPASS = {
  /** Compass Explorer opened for a folder — `metric.compass.explorerOpened` */
  EXPLORER_OPENED: 'compass.explorer.opened',
  /** A review view (compliance / cost / ownership / drift) was opened — `metric.compass.reviewViewOpened` */
  REVIEW_VIEW_OPENED: 'compass.review_view.opened',
  /** Active lens switched (Security / Cost / Ownership / Compliance) — `metric.compass.lensSwitched` */
  LENS_SWITCHED: 'compass.lens.switched',
  /** Drilled focus from a bubble / card into a sub-scope — `metric.compass.focusDrilled` */
  FOCUS_DRILLED: 'compass.focus.drilled',
  /** Spotlight (predicate highlight) applied — `metric.compass.spotlightApplied` */
  SPOTLIGHT_APPLIED: 'compass.spotlight.applied',
  /** Search palette query run */
  SEARCH_RUN: 'compass.search.run',
  /** Compliance audit run — `metric.compass.complianceReportRun` */
  COMPLIANCE_REPORT_RUN: 'compass.compliance.report_run',
  /** Compliance gaps surfaced after a run — `metric.compass.gapsFound` */
  GAPS_FOUND: 'compass.compliance.gaps_found',
  /** A compliance gap was resolved — `metric.compass.gapsResolved` */
  GAP_RESOLVED: 'compass.compliance.gap_resolved',
  /** Evidence artifact attached to a finding / control — `metric.compass.evidenceAttached` */
  EVIDENCE_ATTACHED: 'compass.evidence.attached',
  /** A review finding was resolved — `metric.compass.findingsResolved` */
  FINDING_RESOLVED: 'compass.finding.resolved',
  /** A review item changed status (accept / reject / defer) — `metric.compass.reviewItemStatusChanged` */
  REVIEW_ITEM_STATUS_CHANGED: 'compass.review.item_status_changed',
  /** Durable review item reached a terminal resolved state — `metric.compass.reviewItemResolved` */
  REVIEW_ITEM_RESOLVED: 'compass.review.item_resolved',
  /** Current state accepted as compliance baseline — `metric.compass.baselineAccepted` */
  BASELINE_ACCEPTED: 'compass.baseline.accepted',
  /** Drift from accepted baseline detected — `metric.compass.driftDetected` */
  DRIFT_DETECTED: 'compass.drift.detected',
  /** Audit run finalized — `metric.compass.auditRunFinalized` */
  AUDIT_RUN_FINALIZED: 'compass.audit.run_finalized',
  /** Commercial audit run requested — `metric.compass.auditRunStarted` */
  AUDIT_RUN_STARTED: 'compass.audit.run_started',
  /** Commercial audit snapshot completed — `metric.compass.auditRunCompleted` */
  AUDIT_RUN_COMPLETED: 'compass.audit.run_completed',
  /** Commercial audit snapshot failed — `metric.compass.auditRunFailed` */
  AUDIT_RUN_FAILED: 'compass.audit.run_failed',
  /** Auditor bundle exported — `metric.compass.auditBundleExported` */
  AUDIT_BUNDLE_EXPORTED: 'compass.audit.bundle_exported',
  /** Auditor bundle generated for download — `metric.compass.auditExportGenerated` */
  AUDIT_EXPORT_GENERATED: 'compass.audit.export_generated',
  /** Audit run drift surfaced — `metric.compass.auditRunDriftDetected` */
  AUDIT_RUN_DRIFT_DETECTED: 'compass.audit.drift_detected',
  /** Durable reviewer decision saved — `metric.compass.auditReviewerDecisionSaved` */
  AUDIT_REVIEWER_DECISION_SAVED: 'compass.audit.reviewer_decision_saved',
  /** Source-health warning/blocker surfaced — `metric.compass.auditSourceHealthWarning` */
  AUDIT_SOURCE_HEALTH_WARNING: 'compass.audit.source_health_warning',
  /** User hit an audit permission or rollout denial — `metric.compass.auditPermissionDenial` */
  AUDIT_PERMISSION_DENIAL: 'compass.audit.permission_denial',
  /** AWS Well-Architected pillar review completed — `metric.compass.wellArchitectedReviewCompleted` */
  WELL_ARCHITECTED_REVIEW_COMPLETED: 'compass.well_architected.review_completed',
  /** Cross-org federation grant created — `metric.compass.federationGranted` */
  FEDERATION_GRANTED: 'compass.federation.granted',
  /** Bundled compliance framework imported into a GRC folder — `metric.compass.frameworkSeeded` */
  FRAMEWORK_SEEDED: 'compass.framework.seeded',
  /** Compass empty-state link tile clicked — `metric.compass.sourceLinkRequested` */
  SOURCE_LINK_REQUESTED: 'compass.source.link_requested',
  /** Compass demo orchestration started — `metric.compass.demoStarted` */
  DEMO_STARTED: 'compass.demo.started',
  /** Inbound federation grant approved — `metric.compass.federationApproved` */
  FEDERATION_APPROVED: 'compass.federation.approved',
  /** Federated view loaded for a joint audit — `metric.compass.federatedViewLoaded` */
  FEDERATED_VIEW_LOADED: 'compass.federated_view.loaded',
  /** Cloud / data source connected to the folder graph — `metric.compass.sourceConnected` */
  SOURCE_CONNECTED: 'compass.source.connected',
  /** Ingestion scan completed (change set produced) — `metric.compass.scanCompleted` */
  SCAN_COMPLETED: 'compass.scan.completed',
  /** Folder coverage % crossed a threshold — `metric.compass.folderCoveragePctReached` */
  COVERAGE_THRESHOLD_REACHED: 'compass.coverage.threshold_reached',
  /** Cost waste identified (zombies / oversize / untagged) — `metric.compass.costWasteIdentified` */
  COST_WASTE_IDENTIFIED: 'compass.cost.waste_identified',
  /** Ownership gaps identified (unowned resources) — `metric.compass.ownershipGapsIdentified` */
  OWNERSHIP_GAPS_IDENTIFIED: 'compass.ownership.gaps_identified',
  /** Stakeholder briefing exported — `metric.compass.briefingExported` */
  BRIEFING_EXPORTED: 'compass.briefing.exported',
  /** Scenario (Terraform / CFN plan diff) reviewed — `metric.compass.scenarioReviewed` */
  SCENARIO_REVIEWED: 'compass.scenario.reviewed',
  /** Reviewed scenario approved — `metric.compass.scenarioApproved` */
  SCENARIO_APPROVED: 'compass.scenario.approved',
  /** Approved scenario applied as a change set — `metric.compass.scenarioApplied` */
  SCENARIO_APPLIED: 'compass.scenario.applied',
  /** Named flow overlay authored on the canvas — `metric.compass.flowOverlayAuthored` */
  FLOW_OVERLAY_AUTHORED: 'compass.flow_overlay.authored',
  /** Flow overlay shared via deep link — `metric.compass.flowOverlayShared` */
  FLOW_OVERLAY_SHARED: 'compass.flow_overlay.shared',
  /** Compass deep link opened (review-state restoration) — `metric.compass.deepLinkOpened` */
  DEEP_LINK_OPENED: 'compass.deep_link.opened',
  /** Knowledge change set accepted (Phase E review) */
  CHANGE_SET_ACCEPTED: 'compass.change_set.accepted',
  /** Knowledge change set rejected */
  CHANGE_SET_REJECTED: 'compass.change_set.rejected',
  /** Knowledge change set deferred */
  CHANGE_SET_DEFERRED: 'compass.change_set.deferred',
  /** Folder-scoped Process Library viewpoint browsed */
  PROCESS_REPOSITORY_BROWSED: 'compass.process_repository.browsed',
  /** Dependency / impact viewed for a graph-backed process */
  PROCESS_REPOSITORY_IMPACT_VIEWED: 'compass.process_repository.impact_viewed',
} as const;

// ==========================================
// MEDIA EVENTS
// ==========================================
export const MEDIA = {
  /** Presigned upload URL request started (payload includes `mediaKind`) */
  UPLOAD_URL_REQUESTED: 'media.upload_url.requested',
  /** Upload flow started after client-side processing (payload includes `mediaKind`) */
  UPLOAD_STARTED: 'media.upload.started',
  /** Upload flow completed after verify-object succeeded (payload includes `mediaKind`) */
  UPLOAD_COMPLETED: 'media.upload.completed',
  /** Upload flow failed at one of the staged boundaries (payload includes `mediaKind` + normalized `failureReason`) */
  UPLOAD_FAILED: 'media.upload.failed',
  /** Upload failed specifically during verify-object (payload includes `mediaKind` + normalized `failureReason`) */
  VERIFY_FAILED: 'media.upload.verify_failed',
  /** CSV photo URL ingest normalization completed */
  CSV_PHOTO_IMPORT_COMPLETED: 'media.csv_photo.imported',
  /** CSV photo URL ingest normalization had one or more failures */
  CSV_PHOTO_IMPORT_FAILED: 'media.csv_photo.import_failed',
  /** Canvas image placement completed */
  CANVAS_IMAGE_ADDED: 'canvas.image.added',
  /** Canvas image added through desktop drop */
  CANVAS_IMAGE_DROP: 'canvas.image.drop',
  /** Canvas image added from clipboard paste */
  CANVAS_IMAGE_PASTE: 'canvas.image.paste',
  /** Canvas image added from file picker */
  CANVAS_IMAGE_PICKER: 'canvas.image.picker',
  /** Canvas image crop committed */
  CANVAS_IMAGE_CROPPED: 'canvas.image.cropped',
  /** Canvas image properties changed from property panel */
  CANVAS_IMAGE_PROPERTIES_EDITED: 'canvas.image.properties_edited',
} as const;

// ==========================================
// EXPORT JOB OUTCOME EVENTS
// ==========================================
export const EXPORT_JOBS = {
  /** Async export job reached completed status */
  COMPLETED: 'export.job.completed',
  /** Async export job reached failed status */
  FAILED: 'export.job.failed',
} as const;

// ==========================================
// SESSION METRICS (Aggregated in session.summary)
// ==========================================
export const SESSION_METRICS = {
  SHAPES_CREATED: 'shapes_created',
  CONNECTORS_CREATED: 'connectors_created',
  QUICK_ADDS: 'quick_adds',
  FIELDS_EDITED: 'fields_edited',
  DURATION_MS: 'duration_ms',
} as const;
